/*--external-- */
import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";

/**--internal-- */
import {
  Button,
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  IconButton,
  PageHeader,
} from "@toddle-edu/ds-web";
import {
  CircleOutlined,
  CornersOutOutlined,
  DragOutlined,
  GoalsOutlined,
  ImageSquareOutlined,
  PenOutlined,
  RectangleOutlined,
  TagOutlined,
  TextOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import { CreateDropDown } from "Attachment";
import { QUESTION_UID_LABELLING } from "Worksheet/modules/WorksheetConstants";
import QuestionDetailsSideBar from "Worksheet/components/QuestionDetailsSideBar";
import { getUserType } from "modules/CommonSelectors";
import { getStyleStrippedText } from "Utils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

/**--relative--*/
import classes from "./DragAndDropCanvasHeader.scss";
import { getImageSizeWithAspectRatio } from "../DragAndDrop/DragAndDropUtils";
import {
  DRAG_AND_DROP_CATEGORY,
  DRAG_AND_DROP_ITEM_TYPE,
} from "../DragAndDrop/DragAndDropConstant";
import {
  getQuestionPreviewDetail,
  getQuestionPreviewDetailV2,
} from "./DragAndDropCanvasHeaderUtils";

const DROPDOWN_OPTIONS = [
  { key: "upload_device", label: "upload_device" },
  { key: "add_google_drive", label: "add_google_drive" },
  { key: "add_one_drive", label: "add_one_drive" },
  { key: "search_on_pixabay", label: "common:search_on_pixabay" },
];

/**
 *
 * @param { t, onSelect }
 * @returns Drag zone button
 */
const AddDragZoneDropdownButton = ({
  t,
  buttonTitle = "",
  isDragAndDrop = false,
  onSelect = () => {},
}) => {
  const type = DRAG_AND_DROP_CATEGORY.DROPZONE;

  const options = [
    {
      key: `${type}#${DRAG_AND_DROP_ITEM_TYPE.RECTANGLE}`,
      label: t("worksheet:rectangle"),
      icon: <RectangleOutlined />,
    },
    {
      key: `${type}#${DRAG_AND_DROP_ITEM_TYPE.ELLIPSE}`,
      label: t("worksheet:ellipse"),
      icon: <CircleOutlined />,
    },
    {
      key: `${type}#${DRAG_AND_DROP_ITEM_TYPE.POLYGON}`,
      label: t("worksheet:polygon"),
      icon: <PenOutlined />,
    },
  ];

  return (
    <Dropdown
      overlay={
        <DropdownMenu
          options={options}
          onClick={onSelect}
          testId="worksheet-dragAndDropCanvasHeader-dragZoneOptions"
        />
      }
    >
      <ButtonDisclosure
        dsVersion="2.0"
        variant="neutral"
        type="outlined"
        size="small"
        _legacy_variant="outlined-subtle"
        icon={
          isDragAndDrop ? (
            <DragOutlined size="xxx-small" />
          ) : (
            <GoalsOutlined size="xxx-small" />
          )
        }
        _legacy_size="small"
        testId="worksheet-dragAndDropCanvasHeader-dragZoneOptionsDropdown"
      >
        {t("common:add_with_label", { label: t(buttonTitle) })}
      </ButtonDisclosure>
    </Dropdown>
  );
};

/**
 *
 * @param {*} param0
 * @returns Drag and Drop question canvas header
 */
const DragAndDropCanvasHeader = ({
  t,
  mode = "",
  config = {},
  isFullscreen = false,
  onAddNewItem = () => {},
  onToggleFullscreen = () => {},
  userType,
  worksheetMode,
  isFromInternalTool,
  isQueriesV2Enabled,
}) => {
  const [showQuestionPreview, setShowQuestionPreview] = useState(false);
  if (!_.includes(["edit", "create", "view"], mode)) return null;

  const isCreateMode = mode === "create";
  const isViewMode = mode === "view";

  const getItemKey = itemType => {
    return `${DRAG_AND_DROP_CATEGORY.DECORATIVE}#${itemType}`;
  };

  // * Upload image and save url into question data
  const onUploadImage = async params => {
    const url = _.get(params, "0.url");
    if (url) {
      const dimension = await getImageSizeWithAspectRatio(url);
      onAddNewItem({
        key: getItemKey(DRAG_AND_DROP_ITEM_TYPE.IMAGE),
        url,
        height: _.get(dimension, "height"),
        width: _.get(dimension, "width"),
      });
    }
  };

  const questionDetail = isQueriesV2Enabled
    ? getQuestionPreviewDetailV2({ t, config, userType, isFromInternalTool })
    : getQuestionPreviewDetail(
        //WORKSHEET_RESTRUCTURING:Remove getQuestionPreviewDetail
        t,
        config,
        userType,
        worksheetMode,
        isFromInternalTool
      );
  const questionTypeUid = _.get(questionDetail, "questionTypeUid");
  const buttonTitle = _.get(
    questionDetail,
    "questionLocales.addElementButtonLabel",
    ""
  );
  const headerTitleLocale = _.get(
    questionDetail,
    "questionLocales.questionPreviewSidebarTitle",
    ""
  );

  const dragAndDropCanvasJsx = (
    <div
      className={classNames({
        [classes.header]: isCreateMode,
        [classes.headerStudent]: !isCreateMode,
        [classes.headerSeparator]: isCreateMode && !isFullscreen,
      })}
    >
      {isCreateMode && (
        <div className={classes.headerLeft}>
          <CreateDropDown
            exportAsPdf={true}
            multiselect={false}
            enableShareModal={false}
            parentType="QuestionField"
            addAttachment={onUploadImage}
            dropDownOptions={DROPDOWN_OPTIONS}
            parentId={_.get(config, "fieldKey", "")}
            accept="image/jpeg,image/png,image/jpg,image/heic,image/heif"
            buttonComponent={
              <Button
                size="small"
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                _legacy_variant="outlined-subtle"
                _legacy_size="small"
                icon={<ImageSquareOutlined size="xxx-small" />}
                testId="worksheet-dragAndDropCanvasHeader-addImageDropdown"
              >
                {t("common:add_with_label", { label: t("common:image") })}
              </Button>
            }
            buttonDropDownContainerStyle={{
              top: 35,
              left: 0,
            }}
            testId="worksheet-dragAndDropCanvasHeader-addImage"
          />

          <Button
            variant="neutral"
            type="outlined"
            dsVersion="2.0"
            size="small"
            _legacy_variant="outlined-subtle"
            _legacy_size="small"
            icon={<TextOutlined size="xxx-small" />}
            onClick={() =>
              onAddNewItem({ key: getItemKey(DRAG_AND_DROP_ITEM_TYPE.TEXT) })
            }
            testId="worksheet-dragAndDropCanvasHeader-addText"
          >
            {t("common:add_with_label", { label: t("unitPlan:text") })}
          </Button>
          {questionTypeUid === QUESTION_UID_LABELLING ? (
            <Button
              variant="neutral"
              type="outlined"
              size="small"
              dsVersion="2.0"
              _legacy_variant="outlined-subtle"
              _legacy_size="small"
              icon={<TagOutlined size="xxx-small" />}
              onClick={() => onAddNewItem({ key: "LABEL" })}
              testId="worksheet-dragAndDropCanvasHeader-addLabel"
            >
              {t("common:add_with_label", { label: t(buttonTitle) })}
            </Button>
          ) : (
            <AddDragZoneDropdownButton
              t={t}
              onSelect={onAddNewItem}
              buttonTitle={buttonTitle}
              isDragAndDrop={_.get(questionDetail, "isDragAndDrop")}
            />
          )}
        </div>
      )}
      {!isFullscreen && (
        <IconButton
          size="small"
          dsVersion="2.0"
          type="outlined"
          variant="neutral"
          _legacy_size="small"
          _legacy_variant="outlined-subtle"
          onClick={onToggleFullscreen}
          icon={<CornersOutOutlined size="xxx-small" />}
          testId="worksheet-dragAndDropCanvasHeader-toggleFullScreen"
        />
      )}
    </div>
  );

  const questionTitleStyle = classNames({
    [classes.questionTitleInHeader]: true,
    "heading-6": true,
  });

  /**
   *
   * If full screen render with page header and align action button in center
   * else return action button aligned left and fullscreen button in right
   */
  return isFullscreen ? (
    <>
      {!isCreateMode && (
        <QuestionDetailsSideBar
          t={t}
          localeLabel={headerTitleLocale}
          viewQuestion={showQuestionPreview}
          setViewQuestion={setShowQuestionPreview}
          questionId={_.get(config, "questionId", "")}
          resourceId={_.get(config, "resourceId", null)}
          questionIndex={_.get(config, "questionIndex", 0)}
          isSubQuestion={_.get(config, "isSubQuestion", false)}
          subQuestionParentId={_.get(config, "subQuestionParentId", null)}
          userType={userType}
          worksheetMode={worksheetMode}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
      )}
      <PageHeader>
        <PageHeader.Left
          onBackClick={onToggleFullscreen}
          title={
            isCreateMode ? (
              t(headerTitleLocale)
            ) : (
              <div
                className={questionTitleStyle}
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: _.get(questionDetail, "questionTitle", ""),
                    type: "SANITIZE_DANGER_HTML_TITLE",
                  }),
                }}
              />
            )
          }
          subtitle={
            isCreateMode ? null : (
              <Button
                dsVersion="2.0"
                variant="neutral"
                size="small"
                type="inline"
                _legacy_size="small"
                _legacy_variant="inline"
                onClick={() => setShowQuestionPreview(prev => !prev)}
                testId="worksheet-dragAndDropCanvasHeader-viewQuestion"
              >
                {t("workbook:view_question")}
              </Button>
            )
          }
          testId="worksheet-dragAndDropCanvasHeader-backButton-pageHeader"
        />
        {isCreateMode && (
          <PageHeader.Middle style={{ alignItems: "center" }}>
            {dragAndDropCanvasJsx}
          </PageHeader.Middle>
        )}
        {!isViewMode && (
          <PageHeader.Right>
            <Button
              variant="progressive"
              dsVersion="2.0"
              size="medium"
              _legacy_size="medium"
              _legacy_variant="progressive"
              onClick={onToggleFullscreen}
              icon={<TickCircleFilled variant="on" size="xx-small" />}
              testId="worksheet-dragAndDropCanvasHeader-save"
            >
              {t("common:save")}
            </Button>
          </PageHeader.Right>
        )}
      </PageHeader>
    </>
  ) : (
    dragAndDropCanvasJsx
  );
};

const mapStateToProps = (state, ownProps) => {
  const { isFromInternalTool } = ownProps;
  const userType = getUserType(state);

  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(
    false,
    isFromInternalTool
  );
  return { userType, isQueriesV2Enabled };
};

export default connect(mapStateToProps, null)(DragAndDropCanvasHeader);

DragAndDropCanvasHeader.defaultProps = {
  t: () => {},
  config: {},
  mode: "preview",
  isFullscreen: false,
  onAddNewItem: () => {},
  onToggleFullscreen: () => {},
  isFromInternalTool: false,
};

DragAndDropCanvasHeader.propTypes = {
  t: PropTypes.func,
  config: PropTypes.object,
  mode: PropTypes.string,
  isFullscreen: PropTypes.bool,
  onAddNewItem: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  isFromInternalTool: PropTypes.bool,
};
